import React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { createQueryString, parseQueryString } from '@toss/utils';
import { z } from 'zod';

import { ACLOSET_SALE_STATUS_CODE_LIST, ACLOSET_SHOP_PLATFORM_TYPE_CODE_LIST } from '@/MetadataType';
import { 상품관리_요청_초기값 } from '@/app/products/common/data';
import type { 상품관리_요청_타입 } from '@/app/products/common/type';

const schema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  searchPeriodType: z.enum(['createDate', 'modifyDate', 'postDate']).optional(),
  itemAlias: z.string().optional(),
  title: z.string().optional(),
  saleStatus: z
    .union([
      z.enum(ACLOSET_SALE_STATUS_CODE_LIST).optional(),
      z.array(z.enum(ACLOSET_SALE_STATUS_CODE_LIST)).optional(),
    ])
    .transform(value => {
      if (typeof value === 'string') {
        return [value];
      }
      return value;
    })
    .optional(),
  platformType: z.enum([...ACLOSET_SHOP_PLATFORM_TYPE_CODE_LIST, '']).optional(),
  pageNumber: z
    .string()
    .transform(value => {
      return parseInt(value);
    })
    .optional(),
  pageSize: z
    .string()
    .transform(value => {
      return parseInt(value);
    })
    .optional(),
  periodDateChip: z.string().optional(),
  supplierName: z.string().optional(),
  multiSearchType: z.enum(['itemAliases', 'titles']),
  multiSearchKeyword: z.string().optional(),
  saleOperation: z.string().optional(),
  aimsCategory: z
    .union([z.string().optional(), z.array(z.string()).optional()])
    .transform(value => {
      if (typeof value === 'string') {
        return [value];
      }
      return value;
    })
    .optional(),
  parentCategory: z.string().optional(),
  isPlatformStatusOnSaleAndStopSale: z
    .string()
    .transform(value => {
      return value === 'true';
    })
    .optional(),

  excelDownloadComplete: z
    .string()
    .transform(val => val === 'true')
    .optional(),

  colorTagID: z
    .union([z.string().optional(), z.array(z.string()).optional()])
    .transform(value => {
      if (typeof value === 'string') {
        return [value];
      }
      return value;
    })
    .optional(),

  sort: z.enum(['asc', 'desc']).optional(),
  sortType: z.enum(['postDate', 'price']).optional(),
});

/**
 * searchParams를 requestType에 맞는 타입으로 변환해서 return
 * XXX: sales 도메인 수정시에 app에 있는 useSalesFilterSearchParams도 함께 수정되어야함.
 */
export default function useSalesFilterSearchParams() {
  const [, setSearchParams] = useSearchParams();

  const location = useLocation();

  const filterSearchParams: 상품관리_요청_타입 = React.useMemo(() => {
    const queryObject = parseQueryString(location.search);

    const validSearchParams = schema.safeParse(queryObject);
    if (!validSearchParams.success) {
      return 상품관리_요청_초기값;
    }

    const data = {
      ...상품관리_요청_초기값,
      ...validSearchParams.data,
    };

    return {
      ...data,
      ...(전체기간_검색인가(data)
        ? {
            to: undefined,
            from: undefined,
            searchPeriodType: undefined,
          }
        : {}),
    };
  }, [location.search]);

  const setFilterSearchParams = (value: Partial<상품관리_요청_타입>) => {
    const data = {
      ...filterSearchParams,
      ...value,
    };

    setSearchParams(
      createQueryString({
        ...data,
        ...(전체기간_검색인가(data)
          ? {
              to: undefined,
              from: undefined,
              searchPeriodType: undefined,
            }
          : {}),
      }),
    );
  };

  return { filterSearchParams, setFilterSearchParams };
}

// 상품코드 검색 시, 전체 기간에서 검색함.
const 전체기간_검색인가 = (data: 상품관리_요청_타입) => {
  return !!data.itemAlias || (data.multiSearchType === 'itemAliases' && !!data.multiSearchKeyword);
};
